import React, { useState, useEffect } from 'react';
import { getDashboardStats, getInvoices, getClients, getServices } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';
import { 
  TrendingUp, 
  Users, 
  FileText, 
  Briefcase,
  Calendar,
  ArrowUp,
  ArrowDown,
  Minus
} from 'lucide-react';
import { toast } from 'sonner';
import PageHelpButton from '../components/help/PageHelpButton';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', {
    useGrouping: 'always', style: 'currency',
    currency: 'EUR'
  }).format(amount);
};

const COLORS = ['#FF6B4A', '#FBBF24', '#0EA5E9', '#10B981', '#F43F5E'];

export const ReportsPage = () => {
  const [stats, setStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  useEffect(() => {
    loadData();
  }, [selectedYear]);

  const loadData = async () => {
    try {
      const [statsRes, invoicesRes, clientsRes, servicesRes] = await Promise.all([
        getDashboardStats(),
        getInvoices({ year: parseInt(selectedYear) }),
        getClients(),
        getServices()
      ]);
      setStats(statsRes.data);
      setInvoices(invoicesRes.data);
      setClients(clientsRes.data);
      setServices(servicesRes.data);
    } catch (e) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Calculate analytics
  const getMonthlyData = () => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthlyData = monthNames.map((name, index) => {
      const monthInvoices = invoices.filter(inv => {
        const invMonth = new Date(inv.issue_date).getMonth();
        return invMonth === index;
      });
      
      const facturado = monthInvoices.reduce((sum, inv) => sum + inv.total, 0);
      const cobrado = monthInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
      const pendiente = facturado - cobrado;
      
      return { name, facturado, cobrado, pendiente, invoices: monthInvoices.length };
    });
    return monthlyData;
  };

  const getClientRanking = () => {
    const clientTotals = {};
    invoices.forEach(inv => {
      if (!clientTotals[inv.client_name]) {
        clientTotals[inv.client_name] = { name: inv.client_name, total: 0, count: 0 };
      }
      clientTotals[inv.client_name].total += inv.total;
      clientTotals[inv.client_name].count += 1;
    });
    
    return Object.values(clientTotals)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  };

  const getStatusDistribution = () => {
    const statusCounts = { paid: 0, sent: 0, draft: 0, overdue: 0, cancelled: 0 };
    invoices.forEach(inv => {
      statusCounts[inv.status] = (statusCounts[inv.status] || 0) + 1;
    });
    
    return [
      { name: 'Pagadas', value: statusCounts.paid, color: '#10B981' },
      { name: 'Enviadas', value: statusCounts.sent, color: '#0EA5E9' },
      { name: 'Borradores', value: statusCounts.draft, color: '#A8A29E' },
      { name: 'Vencidas', value: statusCounts.overdue, color: '#F43F5E' },
      { name: 'Canceladas', value: statusCounts.cancelled, color: '#78716C' },
    ].filter(item => item.value > 0);
  };

  const getQuarterlyComparison = () => {
    const quarters = [
      { name: 'Q1', months: [0, 1, 2] },
      { name: 'Q2', months: [3, 4, 5] },
      { name: 'Q3', months: [6, 7, 8] },
      { name: 'Q4', months: [9, 10, 11] }
    ];
    
    return quarters.map(q => {
      const qInvoices = invoices.filter(inv => {
        const month = new Date(inv.issue_date).getMonth();
        return q.months.includes(month);
      });
      
      return {
        name: q.name,
        total: qInvoices.reduce((sum, inv) => sum + inv.total, 0),
        cobrado: qInvoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0),
        facturas: qInvoices.length
      };
    });
  };

  const calculateGrowth = () => {
    const currentMonth = new Date().getMonth();
    const currentMonthTotal = invoices
      .filter(inv => new Date(inv.issue_date).getMonth() === currentMonth)
      .reduce((sum, inv) => sum + inv.total, 0);
    
    const lastMonth = currentMonth === 0 ? 11 : currentMonth - 1;
    const lastMonthTotal = invoices
      .filter(inv => new Date(inv.issue_date).getMonth() === lastMonth)
      .reduce((sum, inv) => sum + inv.total, 0);
    
    if (lastMonthTotal === 0) return { percentage: 0, trend: 'neutral' };
    
    const growth = ((currentMonthTotal - lastMonthTotal) / lastMonthTotal) * 100;
    return {
      percentage: Math.abs(growth).toFixed(1),
      trend: growth > 0 ? 'up' : growth < 0 ? 'down' : 'neutral'
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B4A]"></div>
      </div>
    );
  }

  const monthlyData = getMonthlyData();
  const clientRanking = getClientRanking();
  const statusDistribution = getStatusDistribution();
  const quarterlyData = getQuarterlyComparison();
  const growth = calculateGrowth();

  const totalFacturado = invoices.reduce((sum, inv) => sum + inv.total, 0);
  const totalCobrado = invoices.filter(inv => inv.status === 'paid').reduce((sum, inv) => sum + inv.total, 0);
  const avgInvoice = invoices.length > 0 ? totalFacturado / invoices.length : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Informes</h1>
            <p className="text-stone-500 text-sm mt-1">Estadísticas y análisis de facturación</p>
          </div>
          <PageHelpButton moduleKey="reports" variant="icon" />
        </div>
        <Select value={selectedYear} onValueChange={setSelectedYear}>
          <SelectTrigger className="w-32 rounded-xl" data-testid="year-select">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[2024, 2025, 2026].map(year => (
              <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6" data-testid="total-invoiced-card">
          <div className="flex items-center gap-2 mb-2">
            <FileText className="w-4 h-4 text-[#FF6B4A]" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Total Facturado</span>
          </div>
          <p className="text-xl sm:text-2xl font-mono font-semibold text-stone-900">
            {formatCurrency(totalFacturado)}
          </p>
          <p className="text-xs text-stone-500 mt-1">{invoices.length} facturas</p>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6" data-testid="total-collected-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-green-600" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Cobrado</span>
          </div>
          <p className="text-xl sm:text-2xl font-mono font-semibold text-green-600">
            {formatCurrency(totalCobrado)}
          </p>
          <p className="text-xs text-stone-500 mt-1">
            {((totalCobrado / (totalFacturado || 1)) * 100).toFixed(0)}% del total
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6" data-testid="avg-invoice-card">
          <div className="flex items-center gap-2 mb-2">
            <Briefcase className="w-4 h-4 text-violet-600" />
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Media/Factura</span>
          </div>
          <p className="text-xl sm:text-2xl font-mono font-semibold text-stone-900">
            {formatCurrency(avgInvoice)}
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6" data-testid="growth-card">
          <div className="flex items-center gap-2 mb-2">
            {growth.trend === 'up' ? (
              <ArrowUp className="w-4 h-4 text-green-600" />
            ) : growth.trend === 'down' ? (
              <ArrowDown className="w-4 h-4 text-red-600" />
            ) : (
              <Minus className="w-4 h-4 text-stone-400" />
            )}
            <span className="text-xs uppercase tracking-[0.15em] font-bold text-stone-500">Crecimiento</span>
          </div>
          <p className={`text-xl sm:text-2xl font-mono font-semibold ${
            growth.trend === 'up' ? 'text-green-600' : growth.trend === 'down' ? 'text-red-600' : 'text-stone-400'
          }`}>
            {growth.trend === 'up' ? '+' : growth.trend === 'down' ? '-' : ''}{growth.percentage}%
          </p>
          <p className="text-xs text-stone-500 mt-1">vs. mes anterior</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Monthly Revenue */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h3 className="text-lg font-heading font-bold mb-4">Facturación Mensual</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={monthlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#78716C' }} />
                <YAxis tick={{ fontSize: 11, fill: '#78716C' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area type="monotone" dataKey="facturado" stackId="1" stroke="#FF6B4A" fill="#FF6B4A" fillOpacity={0.6} name="Facturado" />
                <Area type="monotone" dataKey="cobrado" stackId="2" stroke="#0EA5E9" fill="#0EA5E9" fillOpacity={0.6} name="Cobrado" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Invoice Status Distribution */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h3 className="text-lg font-heading font-bold mb-4">Estado de Facturas</h3>
          <div className="h-64 sm:h-80 flex items-center justify-center">
            {statusDistribution.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {statusDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-stone-500">No hay datos</p>
            )}
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quarterly Comparison */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h3 className="text-lg font-heading font-bold mb-4">Comparativa Trimestral</h3>
          <div className="h-64 sm:h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={quarterlyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#78716C' }} />
                <YAxis tick={{ fontSize: 11, fill: '#78716C' }} tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                <Bar dataKey="total" fill="#FF6B4A" name="Facturado" radius={[4, 4, 0, 0]} />
                <Bar dataKey="cobrado" fill="#0EA5E9" name="Cobrado" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Clients */}
        <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
          <h3 className="text-lg font-heading font-bold mb-4">Top 5 Clientes</h3>
          {clientRanking.length > 0 ? (
            <div className="space-y-4">
              {clientRanking.map((client, index) => (
                <div key={client.name} className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center">
                    <span className="text-sm font-bold text-[#FF6B4A]">{index + 1}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-stone-900 truncate">{client.name}</p>
                    <p className="text-xs text-stone-500">{client.count} facturas</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold text-stone-900">{formatCurrency(client.total)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-48">
              <div className="text-center">
                <Users className="w-12 h-12 mx-auto mb-2 text-stone-300" />
                <p className="text-stone-500">No hay datos de clientes</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-stone-100 p-4 sm:p-6">
        <h3 className="text-lg font-heading font-bold mb-4">Resumen del Año</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
          <div className="text-center p-4 bg-stone-50 rounded-xl">
            <p className="text-3xl sm:text-4xl font-mono font-bold text-[#FF6B4A]">{invoices.length}</p>
            <p className="text-sm text-stone-500 mt-1">Facturas</p>
          </div>
          <div className="text-center p-4 bg-stone-50 rounded-xl">
            <p className="text-3xl sm:text-4xl font-mono font-bold text-[#FF6B4A]">{clients.length}</p>
            <p className="text-sm text-stone-500 mt-1">Clientes</p>
          </div>
          <div className="text-center p-4 bg-stone-50 rounded-xl">
            <p className="text-3xl sm:text-4xl font-mono font-bold text-[#FF6B4A]">{services.length}</p>
            <p className="text-sm text-stone-500 mt-1">Servicios</p>
          </div>
          <div className="text-center p-4 bg-stone-50 rounded-xl">
            <p className="text-3xl sm:text-4xl font-mono font-bold text-green-600">
              {((totalCobrado / (totalFacturado || 1)) * 100).toFixed(0)}%
            </p>
            <p className="text-sm text-stone-500 mt-1">Tasa de Cobro</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
